import React from "react";
import { Redirect, Route, Switch, useHistory, useLocation } from "react-router-dom";
import { Button, Flex, Text } from "@chakra-ui/react";
import {
  OktonAppShell,
  OktonAuthCallback,
  OktonAuthProvider,
  OktonForgotPassword,
  OktonLogin,
  OktonPlatformProvider,
  createHttpPlatformClient,
  useOktonAuth,
  OktonLoadingState,
  OktonNavSection,
} from "@okton/platform-ui";
import {
  LuBot,
  LuCoins,
  LuCpu,
  LuGauge,
  LuLayers3,
  LuListChecks,
  LuRoute,
} from "react-icons/lu";
import { AiPage } from "./pages/AiPage";

const PLATFORM_BASE_URL = "https://keycloak.okton.com.br/platform-api";
const system = { id: "okton-ai-admin", name: "IA Okton" };

const platformClient = createHttpPlatformClient({
  baseUrl: PLATFORM_BASE_URL,
  systemId: system.id,
});

const menu: OktonNavSection[] = [
  {
    id: "geral",
    title: "GERAL",
    items: [
      { id: "overview", label: "Visão geral", href: "/", exact: true, icon: LuGauge },
      { id: "budget", label: "Orçamento", href: "/budget", icon: LuCoins },
    ],
  },
  {
    id: "infraestrutura",
    title: "INFRAESTRUTURA",
    items: [
      { id: "provider", label: "Modelos e tarifas", href: "/provider", icon: LuCpu },
      { id: "router", label: "AI Router", href: "/router", icon: LuRoute },
      { id: "learning", label: "Aprendizado", href: "/learning", icon: LuBot },
      { id: "systems", label: "IA por sistema", href: "/systems", icon: LuLayers3 },
    ],
  },
  {
    id: "operacao",
    title: "OPERAÇÃO",
    items: [
      { id: "pilot", label: "Teste do piloto", href: "/pilot", icon: LuBot },
      { id: "reconciliation", label: "Conferências", href: "/reconciliation", icon: LuListChecks },
      { id: "plans", label: "Planos de IA", href: "/plans", icon: LuBot },
    ],
  },
];

const LoadingScreen: React.FC<{ message?: string }> = ({
  message = "Carregando acesso...",
}) => <OktonLoadingState title={message} minH="100vh" />;

const PublicOnlyRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useOktonAuth();
  if (isLoading) return <LoadingScreen />;
  if (isAuthenticated) return <Redirect to="/" />;
  return <>{children}</>;
};

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading, accessToken, logout } = useOktonAuth();
  const location = useLocation();
  const [accessState, setAccessState] = React.useState<"checking" | "allowed" | "denied">("checking");

  React.useEffect(() => {
    if (!isAuthenticated || !accessToken) {
      setAccessState("checking");
      return;
    }
    let active = true;
    const checkAccess = platformClient.checkSystemAccess;
    if (!checkAccess) {
      setAccessState("denied");
      return undefined;
    }

    checkAccess(system.id)
      .then((allowed) => {
        if (active) setAccessState(allowed ? "allowed" : "denied");
      })
      .catch(() => {
        if (active) setAccessState("denied");
      });
    return () => { active = false; };
  }, [isAuthenticated, accessToken]);

  if (isLoading) return <LoadingScreen />;
  if (!isAuthenticated) {
    return <Redirect to={{ pathname: "/login", state: { from: location.pathname } }} />;
  }
  if (accessState === "checking") return <LoadingScreen message="Validando permissões..." />;
  if (accessState === "denied") {
    return (
      <Flex minH="100vh" align="center" justify="center" direction="column" gap="4">
        <Text fontWeight="600">Acesso não autorizado</Text>
        <Text color="gray.500">Seu usuário não possui acesso ao painel administrativo da IA Okton.</Text>
        <Button onClick={() => logout()}>Sair</Button>
      </Flex>
    );
  }
  return <>{children}</>;
};

const ApplicationContent: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const { user, bootstrap, logout } = useOktonAuth();
  if (!user) return <LoadingScreen message="Carregando usuário..." />;

  const features = {
    billing: false,
    profile: bootstrap?.features.profile !== false,
    branchSwitching: false,
    notifications: Boolean(bootstrap?.features.notifications),
  };

  return (
    <OktonPlatformProvider system={system} user={user} features={features} client={platformClient}>
      <OktonAppShell
        menu={menu}
        onLogout={() => logout()}
        pathname={location.pathname}
        onNavigate={(href) => history.push(href)}
      >
        <Switch>
          <Route exact path="/"><AiPage section="overview" /></Route>
          <Route exact path="/budget"><AiPage section="budget" /></Route>
          <Route exact path="/provider"><AiPage section="provider" /></Route>
          <Route exact path="/router"><AiPage section="router" /></Route>
          <Route exact path="/learning"><AiPage section="learning" /></Route>
          <Route exact path="/systems"><AiPage section="systems" /></Route>
          <Route exact path="/pilot"><AiPage section="pilot" /></Route>
          <Route exact path="/reconciliation"><AiPage section="reconciliation" /></Route>
          <Route exact path="/plans"><AiPage section="plans" /></Route>
          <Redirect to="/" />
        </Switch>
      </OktonAppShell>
    </OktonPlatformProvider>
  );
};

export const App: React.FC = () => (
  <OktonAuthProvider systemId={system.id} platformBaseUrl={PLATFORM_BASE_URL}>
    <Switch>
      <Route exact path="/auth/callback"><OktonAuthCallback /></Route>
      <Route exact path="/login"><PublicOnlyRoute><OktonLogin /></PublicOnlyRoute></Route>
      <Route exact path="/forgot"><PublicOnlyRoute><OktonForgotPassword /></PublicOnlyRoute></Route>
      <Route><ProtectedRoute><ApplicationContent /></ProtectedRoute></Route>
    </Switch>
  </OktonAuthProvider>
);
